import React, { useState, useEffect, useMemo } from 'react';
import { FormField } from '../../../types/formBuilder';
import { DynamicFormFields } from '../DynamicFormFields';

interface PromptBuilderInputProps {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  appId?: string;
}

export const PromptBuilderInput: React.FC<PromptBuilderInputProps> = ({
  field,
  onChange,
  disabled = false,
  appId,
}) => {
  const [subFieldValues, setSubFieldValues] = useState<Record<string, any>>({});
  const showSubFields = true;

  const config = field.promptBuilderConfig;

  // Initialize sub-field values with defaults
  useEffect(() => {
    if (config?.sub_fields) {
      const initialValues: Record<string, any> = {};
      config.sub_fields.forEach(subField => {
        const fieldKey = subField.variable_name || subField.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
        if (subField.defaultValue !== undefined) {
          initialValues[fieldKey] = subField.defaultValue;
        }
      });
      setSubFieldValues(initialValues);
    }
  }, [config?.sub_fields]);

  // Construct the final prompt by replacing placeholders
  const constructedPrompt = useMemo(() => {
    if (!config?.template) {
      return '';
    }

    let prompt = config.template;
    
    // Replace placeholders with actual values
    if (config.sub_fields) {
      config.sub_fields.forEach(subField => {
        const fieldKey = subField.variable_name || subField.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
        const placeholder = `{${fieldKey}}`;
        const value = subFieldValues[fieldKey] || '';
        prompt = prompt.replace(new RegExp(placeholder, 'g'), value);
      });
    }

    return prompt;
  }, [config?.template, config?.sub_fields, subFieldValues]);

  // Update the main field value when constructed prompt changes
  useEffect(() => {
    onChange(constructedPrompt);
  }, [constructedPrompt, onChange]);

  const handleSubFieldChange = (fieldKey: string, fieldValue: any) => {
    setSubFieldValues(prev => ({
      ...prev,
      [fieldKey]: fieldValue
    }));
  };

  if (!config?.sub_fields || config.sub_fields.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No sub-fields configured for this prompt builder.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showSubFields && (
        <div className="space-y-3">
          <DynamicFormFields
            fields={config.sub_fields}
            values={subFieldValues}
            onChange={handleSubFieldChange}
            disabled={disabled}
            appId={appId}
          />
        </div>
      )}
    </div>
  );
};